import React from 'react'
import { Search, MapPin, Grid, List, ChevronDown, Building, Calendar } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import type { useTurbineManagementViewModel } from '../useTurbineManagementViewModel'

type VM = ReturnType<typeof useTurbineManagementViewModel>

interface TurbineFiltersProps {
  dropdownRef: VM['dropdownRef']
  isDropdownOpen: VM['isDropdownOpen']
  setIsDropdownOpen: VM['setIsDropdownOpen']
  cityOptions: VM['cityOptions']
  selectedCity: VM['selectedCity']
  setSelectedCity: VM['setSelectedCity']
  isSuperAdmin: VM['isSuperAdmin']
  companyDropdownRef: VM['companyDropdownRef']
  isCompanyDropdownOpen: VM['isCompanyDropdownOpen']
  setIsCompanyDropdownOpen: VM['setIsCompanyDropdownOpen']
  selectedCompanyId: VM['selectedCompanyId']
  setSelectedCompanyId: VM['setSelectedCompanyId']
  companies: VM['companies']
  yearDropdownRef: VM['yearDropdownRef']
  isYearDropdownOpen: VM['isYearDropdownOpen']
  setIsYearDropdownOpen: VM['setIsYearDropdownOpen']
  yearOptions: VM['yearOptions']
  selectedYear: VM['selectedYear']
  setSelectedYear: VM['setSelectedYear']
  searchTerm: VM['searchTerm']
  setSearchTerm: VM['setSearchTerm']
  viewMode: VM['viewMode']
  setViewMode: VM['setViewMode']
}

const TurbineFilters: React.FC<TurbineFiltersProps> = ({
  dropdownRef,
  isDropdownOpen,
  setIsDropdownOpen,
  cityOptions,
  selectedCity,
  setSelectedCity,
  isSuperAdmin,
  companyDropdownRef,
  isCompanyDropdownOpen,
  setIsCompanyDropdownOpen,
  selectedCompanyId,
  setSelectedCompanyId,
  companies,
  yearDropdownRef,
  isYearDropdownOpen,
  setIsYearDropdownOpen,
  yearOptions,
  selectedYear,
  setSelectedYear,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode,
}) => {
  const { t } = useLanguage()

  return (
        <div className="mb-8 space-y-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-wrap">
              {/* City Filter - Custom Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="bg-white border-2 border-primary-300 rounded-lg px-3 py-2 pl-9 pr-9 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-primary-400 transition-colors text-sm font-medium text-left h-10 flex items-center justify-between min-w-[160px] whitespace-nowrap"
                >
                  <span className="flex items-center whitespace-nowrap">
                    <MapPin className="w-4 h-4 text-primary-500 mr-2 flex-shrink-0" />
                    {cityOptions.find(option => option.value === selectedCity)?.label}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-primary-700 transition-transform flex-shrink-0 ml-2 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                    {cityOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedCity(option.value)
                          setIsDropdownOpen(false)
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-primary-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                          selectedCity === option.value ? 'bg-primary-100 text-primary-700' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Company Filter (SuperAdmin only) */}
              {isSuperAdmin && (
                <div className="relative" ref={companyDropdownRef}>
                <button
                    onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
                  className="bg-white border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400 transition-colors text-sm font-medium text-left h-10 flex items-center justify-between min-w-[180px] whitespace-nowrap"
                >
                  <span className="whitespace-nowrap flex items-center">
                    <Building className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                      {selectedCompanyId === 'all' 
                        ? t.dashboard.allCompanies 
                        : companies.find(c => c.id === selectedCompanyId)?.name || t.dashboard.selectCompany
                      }
                  </span>
                    <ChevronDown className={`w-4 h-4 text-gray-700 transition-transform flex-shrink-0 ml-2 ${isCompanyDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                  {isCompanyDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSelectedCompanyId('all')
                          setIsCompanyDropdownOpen(false)
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-primary-50 first:rounded-t-lg transition-colors ${
                          selectedCompanyId === 'all' ? 'bg-primary-100 text-primary-700' : 'text-gray-700'
                        }`}
                      >
                        {t.dashboard.allCompanies}
                      </button>
                      {companies.map(company => (
                        <button
                          key={company.id}
                          onClick={() => {
                            setSelectedCompanyId(company.id)
                            setIsCompanyDropdownOpen(false)
                          }}
                          className={`w-full px-4 py-2 text-left hover:bg-primary-50 last:rounded-b-lg transition-colors ${
                            selectedCompanyId === company.id ? 'bg-primary-100 text-primary-700' : 'text-gray-700'
                        }`}
                      >
                          {company.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Year Filter */}
              <div className="relative" ref={yearDropdownRef}>
                <button
                  onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                  className="bg-white border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400 transition-colors text-sm font-medium text-left h-10 flex items-center justify-between min-w-[120px] whitespace-nowrap"
                >
                  <span className="whitespace-nowrap flex items-center">
                    <Calendar className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                    {yearOptions.find(option => option.value === selectedYear)?.label}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-700 transition-transform flex-shrink-0 ml-2 ${isYearDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isYearDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto">
                    {yearOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedYear(option.value as number | 'all')
                          setIsYearDropdownOpen(false)
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-primary-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                          selectedYear === option.value ? 'bg-primary-100 text-primary-700' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t.dashboard.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[300px]"
                />
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

        </div>
  )
}

export default TurbineFilters
